/**
 * Header.jsx - Navigation Header
 * 100% Blueprint CMS driven - NO hardcoded text
 * Dark premium theme
 */

import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../App';
import supabase from '../lib/supabase';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { 
  LogOut, 
  Settings, 
  User, 
  Menu, 
  X,
  LayoutDashboard, 
  ChevronDown,
  Sparkles,
  HelpCircle,
  Building2,
  ExternalLink
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import {
  Sheet,
  SheetContent,
  SheetTrigger,
  SheetClose,
} from './ui/sheet';

const Header = ({ transparent = false }) => {
  const { user, profile, logout, loading: authLoading, isAdmin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Blueprint CMS State
  const [branding, setBranding] = useState({
    site_name: 'SlabReality',
    logo_url: '',
    primary_color: '#b08b5c'
  });
  const [navPages, setNavPages] = useState([]);
  const [userMenuItems, setUserMenuItems] = useState([
    { slug: 'dashboard', label_it: 'Dashboard', path: '/dashboard', icon: 'LayoutDashboard' },
    { slug: 'settings', label_it: 'Impostazioni', path: '/settings', icon: 'Settings' },
    { slug: 'support', label_it: 'Supporto', path: '/support', icon: 'HelpCircle' },
    { slug: 'blueprint', label_it: 'Blueprint CMS', path: '/blueprint', icon: 'LayoutDashboard', requires_admin: true },
    { slug: 'separator', is_separator: true },
    { slug: 'logout', label_it: 'Esci', icon: 'LogOut', action: 'logout' }
  ]);
  const [loading, setLoading] = useState(true);
  const [mobileOpen, setMobileOpen] = useState(false);

  // Fetch branding and navigation from Blueprint CMS
  useEffect(() => {
    const fetchBlueprintData = async () => {
      try {
        // Try RPC first (if available)
        const { data: rpcData, error: rpcError } = await supabase.rpc('get_blueprint_config', { p_lang: 'it' });
        
        if (!rpcError && rpcData) {
          // Use RPC response
          console.log('Header: Using Blueprint RPC config');
          
          if (rpcData.branding) {
            setBranding({
              site_name: rpcData.branding.site_name || 'SlabReality',
              logo_url: rpcData.branding.logo_url || '',
              logo_text: rpcData.branding.logo_text || 'SR',
              logo_full: rpcData.branding.logo_full || 'SLABREALITY',
              primary_color: rpcData.branding.primary_color || '#b08b5c'
            });
          }
          
          if (rpcData.navbar?.links && rpcData.navbar.links.length > 0) {
            setNavPages(rpcData.navbar.links.map(link => ({
              slug: link.slug,
              title: link.title,
              title_it: link.title,
              icon: link.icon,
              requires_auth: link.requires_auth
            })));
            setLoading(false);
            return;
          }
        }
        
        // Fallback: Direct table query
        console.log('Header: RPC failed, trying direct query');
        
        // Fetch branding
        const { data: brandingData } = await supabase
          .from('blueprint_branding')
          .select('*')
          .limit(1)
          .single();
        
        if (brandingData) {
          setBranding(brandingData);
        }

        // Fetch top menu pages
        const { data: pagesData } = await supabase
          .from('blueprint_pages')
          .select('slug, title, title_it, icon, requires_auth')
          .eq('show_in_top_menu', true)
          .eq('is_published', true)
          .order('top_menu_order', { ascending: true });
        
        if (pagesData && pagesData.length > 0) {
          setNavPages(pagesData);
        } else {
          // Final fallback: Default navigation items
          console.log('Header: No CMS pages found, using default navigation');
          setNavPages([
            { slug: 'materials', title: 'Materials', title_it: 'Materiali', requires_auth: false },
            { slug: 'visual-search', title: 'Visual Search', title_it: 'Ricerca Visiva', requires_auth: false },
            { slug: 'dna-compare', title: 'DNA Compare', title_it: 'Confronta DNA', requires_auth: false },
            { slug: 'inspiration', title: 'Inspiration', title_it: 'Ispirazione', requires_auth: false },
            { slug: 'retail-advisor', title: 'Retail & Advisor', title_it: 'Retail & Advisor', requires_auth: false }
          ]);
        }

        // Fetch user menu items from Blueprint
        const { data: menuData, error: menuError } = await supabase
          .from('blueprint_user_menu_items')
          .select('*')
          .eq('is_active', true)
          .order('order_index', { ascending: true });
        
        if (!menuError && menuData && menuData.length > 0) {
          setUserMenuItems(menuData);
        } else {
          // Default user menu items (fallback)
          setUserMenuItems([
            { slug: 'dashboard', label_it: 'Dashboard', path: '/dashboard', icon: 'LayoutDashboard' },
            { slug: 'settings', label_it: 'Impostazioni', path: '/settings', icon: 'Settings' },
            { slug: 'support', label_it: 'Supporto', path: '/support', icon: 'HelpCircle' },
            { slug: 'blueprint', label_it: 'Blueprint CMS', path: '/blueprint', icon: 'LayoutDashboard', requires_admin: true },
            { slug: 'separator', is_separator: true },
            { slug: 'logout', label_it: 'Esci', icon: 'LogOut', action: 'logout' }
          ]);
        }
      } catch (err) {
        console.error('Header: Error fetching Blueprint data:', err);
        // Use defaults on error
        setNavPages([
          { slug: 'materials', title: 'Materials', title_it: 'Materiali', requires_auth: false },
          { slug: 'visual-search', title: 'Visual Search', title_it: 'Ricerca Visiva', requires_auth: false },
          { slug: 'dna-compare', title: 'DNA Compare', title_it: 'Confronta DNA', requires_auth: false },
          { slug: 'inspiration', title: 'Inspiration', title_it: 'Ispirazione', requires_auth: false },
          { slug: 'retail-advisor', title: 'Retail & Advisor', title_it: 'Retail & Advisor', requires_auth: false }
        ]);
        // Also set default user menu items on error
        setUserMenuItems([
          { slug: 'dashboard', label_it: 'Dashboard', path: '/dashboard', icon: 'LayoutDashboard' },
          { slug: 'settings', label_it: 'Impostazioni', path: '/settings', icon: 'Settings' },
          { slug: 'support', label_it: 'Supporto', path: '/support', icon: 'HelpCircle' },
          { slug: 'blueprint', label_it: 'Blueprint CMS', path: '/blueprint', icon: 'LayoutDashboard', requires_admin: true },
          { slug: 'separator', is_separator: true },
          { slug: 'logout', label_it: 'Esci', icon: 'LogOut', action: 'logout' }
        ]);
      } finally {
        setLoading(false);
      }
    };

    fetchBlueprintData();
  }, []);

  const handleLogout = async () => {
    try {
      await logout();
      toast.success('Disconnesso con successo');
      navigate('/', { replace: true });
    } catch (err) {
      navigate('/', { replace: true });
    }
  };

  const isActiveLink = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  // Use slug directly as route - NO MAPPING, CMS controls everything
  const getPageRoute = (slug) => {
    return `/${slug}`;
  };

  // Get display title (Italian if available)
  const getPageTitle = (page) => {
    return page.title_it || page.title;
  };

  // Get icon component from string name
  const getMenuIcon = (iconName) => {
    const icons = {
      LayoutDashboard, Settings, HelpCircle, LogOut, User, Building2, Sparkles, ExternalLink
    };
    return icons[iconName] || User;
  };

  const isLoggedIn = !!user;

  // Minimal loading header
  if (authLoading && loading) {
    return (
      <header className="fixed top-0 left-0 right-0 z-50 bg-[#0a0a0f]/95 backdrop-blur-md border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <span className="text-xl font-light tracking-wide text-white">{branding.site_name}</span>
            </Link>
            <div className="w-5 h-5 border-2 border-[#b08b5c] border-t-transparent rounded-full animate-spin" />
          </div>
        </div>
      </header>
    );
  }

  return (
    <header 
      className={`fixed top-0 left-0 right-0 z-50 transition-colors ${
        transparent && location.pathname === '/' 
          ? 'bg-transparent' 
          : 'bg-[#0a0a0f]/95 backdrop-blur-md border-b border-white/5'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 py-4">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link 
            to="/" 
            className="flex items-center gap-2" 
            data-testid="header-logo"
          >
            {branding.logo_url ? (
              <img 
                src={branding.logo_url} 
                alt={branding.site_name} 
                className="h-8 w-auto" 
              />
            ) : (
              <span className="text-xl font-light tracking-wide text-white">
                {branding.site_name}
              </span>
            )}
          </Link>
          
          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-1">
            {/* Dynamic pages from Blueprint CMS */}
            {navPages.map(page => {
              // Skip auth-required pages if not logged in
              if (page.requires_auth && !isLoggedIn) return null;
              
              const route = getPageRoute(page.slug);
              const isActive = isActiveLink(route);
              
              return (
                <Link 
                  key={page.slug}
                  to={route}
                  className={`px-4 py-2 text-sm transition-colors rounded-lg ${
                    isActive 
                      ? 'text-white bg-white/10' 
                      : 'text-white/70 hover:text-white hover:bg-white/5'
                  }`}
                  data-testid={`nav-${page.slug}`}
                >
                  {getPageTitle(page)}
                </Link>
              );
            })}
          </nav>

          {/* Right Side Actions */}
          <div className="flex items-center gap-3">
            {isLoggedIn ? (
              <>
                {/* User Menu */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button 
                      variant="ghost" 
                      className="flex items-center gap-2 text-white/70 hover:text-white hover:bg-white/10"
                      data-testid="user-menu-trigger"
                    >
                      <div className="w-8 h-8 rounded-full bg-[#b08b5c]/20 border border-[#b08b5c]/30 flex items-center justify-center">
                        <span className="text-sm font-medium text-[#b08b5c]">
                          {profile?.first_name?.[0] || user?.email?.[0]?.toUpperCase() || 'U'}
                        </span>
                      </div>
                      <ChevronDown className="w-4 h-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent 
                    align="end" 
                    className="w-56 bg-[#121212] border-white/10 text-white"
                  >
                    <div className="px-3 py-2 border-b border-white/10">
                      <p className="text-sm font-medium">{profile?.full_name || profile?.first_name || 'Utente'}</p>
                      <p className="text-xs text-white/50">{user?.email}</p>
                    </div>
                    
                    {/* Dynamic menu items from Blueprint CMS */}
                    {userMenuItems.map((item) => {
                      // Skip admin-only items if not admin
                      if (item.requires_admin && !isAdmin) return null;
                      
                      // Render separator
                      if (item.is_separator) {
                        return <DropdownMenuSeparator key={item.slug} className="bg-white/10" />;
                      }
                      
                      const Icon = getMenuIcon(item.icon);
                      const isLogoutAction = item.action === 'logout';
                      const isExternalLink = item.target === '_blank';
                      
                      if (isLogoutAction) {
                        return (
                          <DropdownMenuItem 
                            key={item.slug}
                            onClick={handleLogout}
                            className="text-red-400 hover:text-red-300 hover:bg-red-500/10 cursor-pointer"
                            data-testid={`menu-${item.slug}`}
                          >
                            <Icon className="w-4 h-4 mr-2" />
                            {item.label_it}
                          </DropdownMenuItem>
                        );
                      }
                      
                      if (isExternalLink) {
                        return (
                          <DropdownMenuItem 
                            key={item.slug}
                            onClick={() => window.open(item.path, '_blank')}
                            className="text-[#b08b5c] hover:text-[#c9a577] hover:bg-[#b08b5c]/10 cursor-pointer"
                            data-testid={`menu-${item.slug}`}
                          >
                            <Icon className="w-4 h-4 mr-2" />
                            {item.label_it}
                            <ExternalLink className="w-3 h-3 ml-auto opacity-50" />
                          </DropdownMenuItem>
                        );
                      }
                      
                      return (
                        <DropdownMenuItem 
                          key={item.slug}
                          onClick={() => navigate(item.path)}
                          className="text-white/70 hover:text-white hover:bg-white/10 cursor-pointer"
                          data-testid={`menu-${item.slug}`}
                        >
                          <Icon className="w-4 h-4 mr-2" />
                          {item.label_it}
                        </DropdownMenuItem>
                      );
                    })}
                    
                    {/* Professional Dashboard - shown for professional users */}
                    {profile?.is_professional && (
                      <DropdownMenuItem 
                        onClick={() => navigate('/professional')}
                        className="text-[#b08b5c] hover:text-[#c9a577] hover:bg-[#b08b5c]/10 cursor-pointer"
                        data-testid="professional-dashboard-menu-item"
                      >
                        <Building2 className="w-4 h-4 mr-2" />
                        Dashboard Professionista
                      </DropdownMenuItem>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            ) : (
              <>
                {/* Login / Register Buttons */}
                <Link to="/login">
                  <Button 
                    variant="ghost" 
                    className="text-white/70 hover:text-white hover:bg-white/10"
                  >
                    Accedi
                  </Button>
                </Link>
                <Link to="/register">
                  <Button 
                    className="bg-[#b08b5c] hover:bg-[#c9a577] text-white"
                  >
                    Registrati
                  </Button>
                </Link>
              </>
            )}
            
            {/* Mobile Menu Button */}
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <Button 
                  variant="ghost" 
                  size="icon" 
                  className="lg:hidden text-white/70 hover:text-white hover:bg-white/10"
                >
                  <Menu className="w-5 h-5" />
                </Button>
              </SheetTrigger>
              <SheetContent 
                side="right" 
                className="w-80 bg-[#0a0a0f] border-l border-white/10 p-0"
              >
                <div className="flex flex-col h-full">
                  {/* Mobile Header */}
                  <div className="flex items-center justify-between p-6 border-b border-white/10">
                    <span className="text-lg font-light text-white">{branding.site_name}</span>
                    <SheetClose asChild>
                      <Button variant="ghost" size="icon" className="text-white/70 hover:text-white">
                        <X className="w-5 h-5" />
                      </Button>
                    </SheetClose>
                  </div>
                  
                  {/* Mobile Navigation */}
                  <nav className="flex-1 p-4 space-y-1">
                    {navPages.map(page => {
                      if (page.requires_auth && !isLoggedIn) return null;
                      const route = getPageRoute(page.slug);
                      
                      return (
                        <SheetClose key={page.slug} asChild>
                          <Link
                            to={route}
                            className={`block px-4 py-3 rounded-lg transition-colors ${
                              isActiveLink(route)
                                ? 'bg-white/10 text-white'
                                : 'text-white/70 hover:bg-white/5 hover:text-white'
                            }`}
                          >
                            {getPageTitle(page)}
                          </Link>
                        </SheetClose>
                      );
                    })}
                    
                    {isLoggedIn && (
                      <>
                        <div className="h-px bg-white/10 my-4" />
                        <SheetClose asChild>
                          <Link
                            to="/dashboard"
                            className="block px-4 py-3 rounded-lg text-white/70 hover:bg-white/5 hover:text-white"
                          >
                            Dashboard
                          </Link>
                        </SheetClose>
                        <SheetClose asChild>
                          <Link
                            to="/settings"
                            className="block px-4 py-3 rounded-lg text-white/70 hover:bg-white/5 hover:text-white"
                          >
                            Impostazioni
                          </Link>
                        </SheetClose>
                        <SheetClose asChild>
                          <Link
                            to="/support"
                            className="block px-4 py-3 rounded-lg text-white/70 hover:bg-white/5 hover:text-white"
                          >
                            Supporto
                          </Link>
                        </SheetClose>
                      </>
                    )}
                  </nav>
                  
                  {/* Mobile Footer */}
                  <div className="p-4 border-t border-white/10">
                    {isLoggedIn ? (
                      <Button
                        onClick={() => {
                          setMobileOpen(false);
                          handleLogout();
                        }}
                        variant="ghost"
                        className="w-full justify-start text-red-400 hover:text-red-300 hover:bg-red-500/10"
                      >
                        <LogOut className="w-4 h-4 mr-2" />
                        Esci
                      </Button>
                    ) : (
                      <div className="space-y-2">
                        <SheetClose asChild>
                          <Link to="/login" className="block">
                            <Button variant="outline" className="w-full border-white/20 text-white hover:bg-white/10">
                              Accedi
                            </Button>
                          </Link>
                        </SheetClose>
                        <SheetClose asChild>
                          <Link to="/register" className="block">
                            <Button className="w-full bg-[#b08b5c] hover:bg-[#c9a577] text-white">
                              Registrati
                            </Button>
                          </Link>
                        </SheetClose>
                      </div>
                    )}
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
